import {
  NotificationCategoryEnum,
  NotificationPriorityEnum,
  NotificationTypeEnum
} from '../enums/notification-type.enum';
import { NotificationInterface } from '../interfaces/notification.interface';

export function notificationIcon(notification: NotificationInterface): string {
  if (notification.category === NotificationCategoryEnum.STOCK_OUT) return 'circle-x';
  if (notification.notificationType === NotificationTypeEnum.FINANCIAL) return 'wallet-cards';
  if (notification.notificationType === NotificationTypeEnum.INVENTORY) return 'package';
  if (notification.notificationType === NotificationTypeEnum.PURCHASE) return 'shopping-bag';
  if (notification.notificationType === NotificationTypeEnum.SALES) return 'badge-dollar-sign';
  if (notification.notificationType === NotificationTypeEnum.SUCCESS) return 'circle-check';
  return 'bell';
}

export function notificationColorClass(notification: NotificationInterface): string {
  if (notification.priority === NotificationPriorityEnum.CRITICAL) {
    return 'text-red-600 bg-red-50';
  }
  if (
    notification.priority === NotificationPriorityEnum.URGENT ||
    notification.priority === NotificationPriorityEnum.HIGH
  ) {
    return 'text-orange-600 bg-orange-50';
  }
  if (notification.notificationType === NotificationTypeEnum.SUCCESS) {
    return 'text-green-600 bg-green-50';
  }
  return 'text-primary bg-primary-50';
}

/** Páginas fixas de `/vendas/<página>`; só o restante é tratado como id de venda legada. */
const SALES_STATIC_PAGES = new Set(['caixa', 'historico', 'em-andamento']);

export function notificationRoute(notification: NotificationInterface): string | null {
  if (notification.actionUrl?.startsWith('/')) {
    // Só o caminho decide se é uma venda legada; `?aba=...` não faz parte do id.
    const [path] = notification.actionUrl.split('?');
    const legacySaleRoute = path.match(/^\/vendas\/([^/]+)$/);
    return legacySaleRoute && !SALES_STATIC_PAGES.has(legacySaleRoute[1])
      ? `/vendas/${legacySaleRoute[1]}/detalhes`
      : notification.actionUrl;
  }

  if (notification.category === NotificationCategoryEnum.STOCK_OUT) return '/produtos';
  if (notification.notificationType === NotificationTypeEnum.SALES) return '/vendas';
  if (notification.notificationType === NotificationTypeEnum.PURCHASE) return '/compras';
  if (notification.notificationType === NotificationTypeEnum.FINANCIAL) return '/financeiro';
  if (notification.notificationType === NotificationTypeEnum.INVENTORY) return '/estoque';
  return null;
}

export interface NotificationGroupInterface {
  id: 'today' | 'yesterday' | 'week' | 'older';
  label: string;
  items: NotificationInterface[];
}

const GROUP_LABELS: Record<NotificationGroupInterface['id'], string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  week: 'Esta semana',
  older: 'Anteriores'
};

/** Agrupa por dia (calendário local), mantendo a ordem recebida dentro de cada grupo. */
export function groupNotificationsByDay(
  notifications: NotificationInterface[],
  now: Date = new Date()
): NotificationGroupInterface[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 86_400_000;
  const buckets = new Map<NotificationGroupInterface['id'], NotificationInterface[]>();

  for (const notification of notifications) {
    const time = new Date(notification.createdAt).getTime();
    const id: NotificationGroupInterface['id'] =
      time >= startOfToday
        ? 'today'
        : time >= startOfToday - dayMs
          ? 'yesterday'
          : time >= startOfToday - 6 * dayMs
            ? 'week'
            : 'older';
    buckets.set(id, [...(buckets.get(id) ?? []), notification]);
  }

  return (['today', 'yesterday', 'week', 'older'] as const)
    .filter((id) => buckets.has(id))
    .map((id) => ({ id, label: GROUP_LABELS[id], items: buckets.get(id)! }));
}
